import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  User, Mail, Phone, Calendar, ArrowLeft, MapPin,
  Clock, DollarSign, Tag, Edit, MoreHorizontal,
  CheckCircle, XCircle, AlertCircle, History,
  Scissors, ShoppingBag, Smartphone, TrendingUp
} from "lucide-react";
import { getCustomerDetails, CustomerDetailsResponse } from "../../../services/bookings";
import GlobalLoader from "../../../components/ui/GlobalLoader";
import PageHeader from "../../../components/ui/PageHeader";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";

export default function CustomerDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [details, setDetails] = useState<CustomerDetailsResponse['data'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const response = await getCustomerDetails(parseInt(id));
        if (response.success && response.data) {
          setDetails(response.data);
        } else {
          setDetails(response.data || response as any);
        }
      } catch (err) {
        console.error("Failed to load customer details", err);
        setError("Could not load customer details.");
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [id]);

  // Generate trend data based on booking history
  const chartData = useMemo(() => {
    if (!details?.customer?.customer_bookings) return [];

    // Group bookings by month for the last 6 months
    const months = ["Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const monthData = months.map(m => ({ name: m, value: 0 }));

    // Add real counts to mock months for visual impact
    if (details.customer.customer_bookings.length > 0) {
      monthData[monthData.length - 1].value = details.customer.customer_bookings.length;
      monthData[monthData.length - 2].value = Math.max(0, details.customer.customer_bookings.length - 1);
      monthData[monthData.length - 3].value = Math.max(0, details.customer.customer_bookings.length - 2);
    }

    return monthData;
  }, [details]);

  if (loading) {
    return (
      <div className="flex h-[80vh] w-full items-center justify-center bg-gray-50">
        <GlobalLoader description="Loading customer profile..." />
      </div>
    );
  }

  if (error || !details || !details.customer) {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-gray-50 text-center p-6">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Profile Not Found</h2>
        <p className="text-gray-500 mb-6">{error || "The requested customer profile could not be found."}</p>
        <button
          onClick={() => navigate(-1)}
          className="px-6 py-2 bg-[#885ABB] text-white rounded-full hover:bg-[#724b9e] transition-colors"
        >
          Go Back
        </button>
      </div>
    );
  }

  const { customer, stats, totalSpent, serviceSpent, productAmount } = details;

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount);
  };

  const sortedBookings = [...(customer.customer_bookings || [])].sort((a: any, b: any) =>
    new Date(b.date_time || b.start_date).getTime() - new Date(a.date_time || a.start_date).getTime()
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <PageHeader
        title="Customer Profile"
        icon={User}
        className="border-b-2 border-gray-300 shadow-sm"
        actions={
          <button
            onClick={() => navigate(-1)}
            className="flex items-center justify-center gap-2 px-3 lg:px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors w-full sm:w-auto"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>
        }
      />

      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-4 lg:py-8 space-y-6 lg:space-y-8">

        {/* Hero Section */}
        <div className="bg-white rounded-2xl p-6 lg:p-8 border border-gray-200 shadow-sm relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-purple-50 rounded-full blur-3xl -mr-32 -mt-32 opacity-50 hidden lg:block"></div>

          <div className="relative flex flex-col items-center lg:items-start gap-6 lg:gap-8">
            {/* Avatar & Verification State */}
            <div className="flex flex-col items-center lg:items-start lg:flex-row gap-6 lg:gap-8 w-full">
              <div className="relative">
                <div className="w-24 h-24 lg:w-32 lg:h-32 rounded-full bg-[#885ABB]/10 border-4 border-white shadow-md flex items-center justify-center overflow-hidden">
                  {customer.image || customer.user_image_url ? (
                    <img src={customer.image || customer.user_image_url} alt={customer.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-3xl lg:text-4xl font-bold text-[#885ABB]">
                      {(customer.name?.charAt(0) || "?").toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="absolute bottom-1 right-1 group">
                  <div className={`w-5 h-5 lg:w-6 lg:h-6 border-2 lg:border-4 border-white rounded-full ${customer.mobile_verified === 1 ? 'bg-green-500' : 'bg-gray-300'} transition-colors cursor-help`}></div>

                  {/* Verification Tooltip */}
                  <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-900 text-white text-[10px] font-bold rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none shadow-lg z-10">
                    {customer.mobile_verified === 1 ? 'Verified Customer' : 'Unverified Contact'}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                  </div>
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 text-center lg:text-left space-y-3 lg:space-y-4">
                <div>
                  <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 flex flex-wrap items-center justify-center lg:justify-start gap-2 lg:gap-3">
                    {(customer.name && customer.name !== "null") ? customer.name : "Unknown Customer"}
                    {customer.mobile_verified === 1 && (
                      <CheckCircle className="w-5 h-5 text-[#885ABB]" fill="currentColor" stroke="white" />
                    )}
                  </h1>
                  <p className="text-sm lg:text-gray-500 flex items-center justify-center lg:justify-start gap-2 mt-1 font-medium">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    Member since {formatDate(customer.created_at)}
                  </p>
                </div>

                <div className="flex flex-wrap justify-center lg:justify-start gap-2">
                  {customer.skin_type && customer.skin_type !== "null" && (
                    <span className="px-3 py-1 rounded-full bg-purple-50 text-[#885ABB] text-[10px] font-bold uppercase border border-purple-100 italic">
                      Skin: {customer.skin_type}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 w-full">
              <div className="p-3 lg:p-4 rounded-xl bg-gray-50 border border-gray-100 text-center">
                <p className="text-[10px] lg:text-xs font-bold text-gray-400 uppercase tracking-widest">Visits</p>
                <p className="text-lg lg:text-xl font-bold text-gray-900 mt-1">{stats ? (stats.completed + stats.in_progress) : sortedBookings.length}</p>
              </div>
              <div className="p-3 lg:p-4 rounded-xl bg-purple-50 border border-purple-100 text-center">
                <p className="text-[10px] lg:text-xs font-bold text-[#885ABB] uppercase tracking-widest">Spent</p>
                <p className="text-lg lg:text-xl font-bold text-[#885ABB] mt-1">{formatCurrency(totalSpent)}</p>
              </div>
              <div className="p-3 lg:p-4 rounded-xl bg-purple-50/70 border border-purple-100/70 text-center">
                <p className="text-[10px] lg:text-xs font-bold text-[#885ABB]/80 uppercase tracking-widest flex items-center justify-center gap-1">Services</p>
                <p className="text-base lg:text-lg font-bold text-[#885ABB] mt-1">{formatCurrency(serviceSpent)}</p>
              </div>
              <div className="p-3 lg:p-4 rounded-xl bg-purple-50/40 border border-purple-100/40 text-center">
                <p className="text-[10px] lg:text-xs font-bold text-[#885ABB]/60 uppercase tracking-widest flex items-center justify-center gap-1">Products</p>
                <p className="text-base lg:text-lg font-bold text-[#885ABB] mt-1">{formatCurrency(productAmount)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">

          {/* Left Column */}
          <div className="space-y-6">
            {/* Trend Card */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden p-6 hidden lg:block">
              <h3 className="font-semibold text-[#885ABB] mb-6 flex items-center gap-2 text-sm uppercase tracking-wider">
                <TrendingUp size={18} /> Visit Frequency Trend
              </h3>
              <div className="h-[200px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="colorVisits" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#885ABB" stopOpacity={0.1} />
                        <stop offset="95%" stopColor="#885ABB" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10 }} />
                    <YAxis hide />
                    <RechartsTooltip />
                    <Area type="monotone" dataKey="value" stroke="#885ABB" fillOpacity={1} fill="url(#colorVisits)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-purple-100 bg-white">
                <h3 className="font-bold text-[#885ABB] text-sm uppercase tracking-wider">Contact Details</h3>
              </div>
              <div className="p-5 lg:p-6 space-y-4">
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-purple-50 rounded-lg shrink-0">
                    <Mail className="w-4 h-4 text-[#885ABB]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Email Address</p>
                    <p className="text-sm text-gray-900 font-semibold truncate lg:max-w-none">{(customer.email && customer.email !== "null") ? customer.email : "No email"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-purple-50 rounded-lg shrink-0">
                    <Smartphone className="w-4 h-4 text-[#885ABB]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Mobile Number</p>
                    <p className="text-sm text-gray-900 font-semibold truncate lg:max-w-none">{(customer.mobile && customer.mobile !== "null") ? customer.mobile : customer.formatted_mobile || "No mobile"}</p>
                  </div>
                </div>
                {customer.address && customer.address !== "null" && (
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-purple-50 rounded-lg shrink-0">
                      <MapPin className="w-4 h-4 text-[#885ABB]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Address</p>
                      <p className="text-sm text-gray-900 font-semibold lg:max-w-none">{customer.address}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column: History */}
          <div className="lg:col-span-2 space-y-5 lg:space-y-6">
            <div className="flex items-center justify-between px-2 lg:px-0">
              <h3 className="text-lg font-bold text-gray-900">Booking History</h3>
              {sortedBookings.length > 0 && (
                <span className="text-xs font-bold text-[#885ABB] bg-purple-50 px-2 py-1 rounded-lg">
                  {sortedBookings.length} Total
                </span>
              )}
            </div>

            <div className="space-y-4">
              {sortedBookings.length > 0 ? (
                sortedBookings.map((booking: any, idx: number) => (
                  <div key={idx} className="flex gap-4 bg-white p-4 lg:p-5 rounded-2xl border border-gray-100 shadow-sm hover:border-purple-200 hover:shadow-md transition-all">
                    <div className="flex flex-col items-center justify-center p-2 lg:p-3 rounded-xl bg-purple-50/50 border border-purple-100 min-w-[70px] lg:min-w-[80px] h-fit">
                      <span className="text-[10px] lg:text-xs font-bold text-[#885ABB]/60 uppercase">{new Date(booking.date_time).toLocaleString('default', { month: 'short' })}</span>
                      <span className="text-2xl font-black text-[#885ABB] leading-tight">{new Date(booking.date_time).getDate()}</span>
                      <span className="text-[10px] font-bold text-[#885ABB]/40">{new Date(booking.date_time).getFullYear()}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col lg:flex-row lg:justify-between items-start gap-2">
                        <div className="min-w-0">
                          <h4 className="font-bold text-gray-900 truncate">Booking #{booking.id}</h4>
                          <p className="text-xs text-gray-500 font-medium truncate mt-0.5">{booking.location?.name || "Service Appointment"}</p>
                          <div className="flex flex-wrap items-center gap-2 mt-3">
                            <span className="text-[9px] font-bold text-[#885ABB] bg-purple-50 px-2 py-0.5 rounded border border-purple-100 uppercase tracking-wider">{booking.source}</span>
                            <span className={`text-[9px] px-2 py-0.5 rounded uppercase font-black tracking-wider border ${booking.payment_status === 'completed' ? 'bg-green-50 text-green-600 border-green-100' : 'bg-gray-50 text-gray-500 border-gray-100'}`}>
                              {booking.payment_status || 'Unpaid'}
                            </span>
                          </div>
                        </div>
                        <div className="lg:text-right w-full lg:w-auto mt-2 lg:mt-0 pt-3 lg:pt-0 border-t lg:border-none border-gray-50 flex justify-between lg:block">
                          <span className="lg:hidden text-[10px] uppercase font-bold text-gray-400">Amount</span>
                          <p className="font-black text-[#885ABB] text-base lg:text-lg">
                            {booking.amount_to_pay ? formatCurrency(booking.amount_to_pay) : booking.formated_amount_to_pay}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-16 bg-white rounded-2xl border-2 border-dashed border-gray-100">
                  <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <History className="w-8 h-8 text-gray-300" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">No Booking History</h3>
                  <p className="text-sm text-gray-400 mt-1">This customer hasn't made any bookings yet.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
